import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Heart, ShoppingBag, Minus, Plus, ArrowLeft, ChevronLeft, ChevronRight, Bell } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { useWishlist } from "../context/WishlistContext";
import SEO from "../components/SEO";
import { formatPrice, displayColor } from "../utils/formatters";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { addToCart } = useCart();
  const { isInWishlist, addToWishlist, removeFromWishlist } = useWishlist();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [adding, setAdding] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [zoomed, setZoomed] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState("");
  const [notifying, setNotifying] = useState(false);
  const [notified, setNotified] = useState(false);

  const inWishlist = product ? isInWishlist(product.id) : false;

  const handleNotifyMe = async (e) => {
    e.preventDefault();
    if (!notifyEmail) { toast.error("Please enter your email"); return; }
    setNotifying(true);
    try {
      await axios.post(`${API}/notify/stock`, { email: notifyEmail, product_id: product.id });
      toast.success("We'll notify you when it's back in stock!");
      setNotified(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to subscribe");
    } finally {
      setNotifying(false);
    }
  };

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(`${API}/products/${id}`);
        setProduct(response.data);
        if (response.data.sizes?.length) {
          const sbs = response.data.stock_by_size;
          const firstAvailable = sbs 
            ? response.data.sizes.find(s => (sbs[s] || 0) > 0) || response.data.sizes[0]
            : response.data.sizes[0];
          setSelectedSize(firstAvailable);
        }
        if (response.data.colors?.length) setSelectedColor(response.data.colors[0]);
        if (window.fbq) {
          window.fbq('track', 'ViewContent', {
            content_name: response.data.name,
            content_ids: [response.data.id],
            content_type: 'product',
            value: response.data.price,
            currency: 'AUD',
          });
        }
      } catch (error) {
        console.error("Error fetching product:", error);
        toast.error("Product not found");
        navigate("/shop");
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id, navigate]);

  const handleAddToCart = async () => {
    if (!selectedSize || !selectedColor) {
      toast.error("Please select size and color");
      return;
    }

    setAdding(true);
    const success = await addToCart(product.id, quantity, selectedSize, selectedColor);
    setAdding(false);

    if (success) {
      if (window.fbq) {
        window.fbq('track', 'AddToCart', {
          content_name: product.name,
          content_ids: [product.id],
          content_type: 'product',
          value: product.price * quantity,
          currency: 'AUD',
        });
      }
      navigate("/cart");
    } else {
      toast.error("Failed to add to cart");
    }
  };

  const handleWishlistToggle = async () => {
    if (!isAuthenticated) {
      toast.error("Please login to add to wishlist");
      navigate("/auth");
      return;
    }

    if (inWishlist) {
      await removeFromWishlist(product.id);
      toast.success("Removed from wishlist");
    } else {
      await addToWishlist(product.id);
      toast.success("Added to wishlist");
    }
  };

  const colorMap = {
    Black: "#000000",
    Charcoal: "#36454F",
    White: "#FFFFFF",
    Grey: "#808080",
    "Light Gray": "#C0C0C0",
    "Washed Grey": "#A0A0A0",
    Navy: "#000080",
    Yellow: "#FFD700",
    Green: "#4A6741",
    "Black/Yellow": "#1A1A1A",
    "White/Black": "#F5F5F5",
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="loader" />
      </div>
    );
  }

  if (!product) return null;

  return (
    <div className="min-h-screen pt-32 pb-24 px-6 md:px-12" data-testid="product-page">
      <SEO title={product.name} description={product.description} />
      <div className="max-w-[1400px] mx-auto">
        {/* Back Button */}
        <motion.button
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-[#A3A3A3] hover:text-white transition-colors mb-8"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="font-mono text-sm tracking-wider">BACK</span>
        </motion.button>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-7"
          >
            <div
              className="relative aspect-[3/4] overflow-hidden border border-white/10 cursor-pointer"
              onClick={() => setZoomed(!zoomed)}
            >
              {/* Main Image */}
              <img
                src={product.images[currentImageIndex]}
                alt={product.name}
                className={`w-full h-full object-cover transition-transform duration-500 ${zoomed ? "scale-110" : "scale-100"}`}
                data-testid="product-image"
              />

              {/* Front/Back Label */}
              {product.images.length > 1 && (
                <div className="absolute top-6 right-6 bg-black/60 border border-white/20 px-3 py-1 font-mono text-xs tracking-wider text-white/80">
                  {currentImageIndex === 0 ? "FRONT" : "BACK"}
                </div>
              )}
              
              {/* Navigation Arrows */}
              {product.images.length > 1 && (
                <>
                  <button
                    onClick={(e) => { e.stopPropagation(); setCurrentImageIndex(prev => prev === 0 ? product.images.length - 1 : prev - 1); }}
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/60 border border-white/20 flex items-center justify-center hover:bg-[#FFD700] hover:text-black transition-colors"
                    data-testid="prev-image-btn"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); setCurrentImageIndex(prev => prev === product.images.length - 1 ? 0 : prev + 1); }}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/60 border border-white/20 flex items-center justify-center hover:bg-[#FFD700] hover:text-black transition-colors"
                    data-testid="next-image-btn"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                </>
              )}
              
              {/* Image Indicators */}
              {product.images.length > 1 && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                  {product.images.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentImageIndex(idx)}
                      className={`w-3 h-3 rounded-full transition-colors ${idx === currentImageIndex ? 'bg-[#FFD700]' : 'bg-white/40 hover:bg-white/60'}`}
                      data-testid={`image-indicator-${idx}`}
                    />
                  ))}
                </div>
              )}
              
              {product.featured && (
                <div className="absolute top-6 left-6 bg-[#FFD700] text-black px-4 py-2 font-mono text-sm tracking-widest uppercase">
                  Featured
                </div>
              )}
            </div>
            
            {/* Thumbnails */}
            {product.images.length > 1 && (
              <div className="flex gap-3 mt-4">
                {product.images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentImageIndex(idx)}
                    className={`w-20 h-24 border-2 overflow-hidden transition-colors ${idx === currentImageIndex ? 'border-[#FFD700]' : 'border-white/20 hover:border-white/40'}`}
                    data-testid={`thumbnail-${idx}`}
                  >
                    <img src={img} alt={`${product.name} view ${idx + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </motion.div>

          {/* Details */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="lg:col-span-5 space-y-8"
          >
            <div>
              <p className="font-mono text-[#FFD700] text-sm tracking-[0.3em] mb-2">
                {product.category?.toUpperCase()}
              </p>
              <h1 className="font-heading text-4xl md:text-5xl tracking-tight uppercase" data-testid="product-name">
                {product.name}
              </h1>
              <p className="font-mono text-[#FFD700] text-3xl mt-4" data-testid="product-price">
                {formatPrice(product.price)}
              </p>
            </div>

            {/* Product Highlights */}
            <div className="space-y-2" data-testid="product-highlights">
              <div className="flex items-center gap-3 text-[#C0C0C0] text-sm">
                <span className="text-[#FFD700]">&#10003;</span>
                <span>Premium Heavyweight Cotton</span>
              </div>
              <div className="flex items-center gap-3 text-[#C0C0C0] text-sm">
                <span className="text-[#FFD700]">&#10003;</span>
                <span>Stonewashed Finish</span>
              </div>
              <div className="flex items-center gap-3 text-[#C0C0C0] text-sm">
                <span className="text-[#FFD700]">&#10003;</span>
                <span>Oversized Fit</span>
              </div>
            </div>

            {/* Size Selection */}
            <div>
              <p className="font-mono text-sm tracking-wider mb-4">SIZE</p>
              <div className="flex flex-wrap gap-3">
                {product.sizes.map((size) => {
                  const sizeStock = product.stock_by_size ? (product.stock_by_size[size] || 0) : product.stock;
                  const outOfStock = sizeStock <= 0;
                  return (
                    <button
                      key={size}
                      onClick={() => !outOfStock && setSelectedSize(size)}
                      className={`size-option ${selectedSize === size ? "active" : ""} ${outOfStock ? "opacity-30 cursor-not-allowed line-through" : ""}`}
                      data-testid={`size-${size}`}
                      disabled={outOfStock}
                    >
                      {size}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Color Selection */}
            <div>
              <p className="font-mono text-sm tracking-wider mb-4">COLOR: {displayColor(selectedColor)}</p>
              <div className="flex flex-wrap gap-3">
                {product.colors.map((color) => (
                  <button
                    key={color}
                    onClick={() => setSelectedColor(color)}
                    className={`color-swatch ${selectedColor === color ? "active" : ""}`}
                    style={{ backgroundColor: colorMap[color] || "#808080" }}
                    title={color}
                    data-testid={`color-${color}`}
                  />
                ))}
              </div>
            </div>

            {/* Quantity & Actions */}
            {product.stock > 0 ? (
              <>
                <div>
                  <p className="font-mono text-sm tracking-wider mb-4">QUANTITY</p>
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="w-12 h-12 border border-white/20 flex items-center justify-center hover:border-[#FFD700] transition-colors"
                      data-testid="qty-decrease"
                    >
                      <Minus className="w-5 h-5" />
                    </button>
                    <span className="font-mono text-xl w-12 text-center" data-testid="qty-value">{quantity}</span>
                    <button
                      onClick={() => setQuantity(quantity + 1)}
                      className="w-12 h-12 border border-white/20 flex items-center justify-center hover:border-[#FFD700] transition-colors"
                      data-testid="qty-increase"
                    >
                      <Plus className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                <div className="flex gap-4 pt-4">
                  <button
                    onClick={handleAddToCart}
                    disabled={adding}
                    className="btn-primary flex-1 flex items-center justify-center gap-3"
                    data-testid="add-to-cart-btn"
                  >
                    {adding ? (
                      <div className="loader w-5 h-5 border-2" />
                    ) : (
                      <>
                        <ShoppingBag className="w-5 h-5" />
                        Add to Cart
                      </>
                    )}
                  </button>
                  <button
                    onClick={handleWishlistToggle}
                    className={`w-14 h-14 flex items-center justify-center border-2 transition-colors ${
                      inWishlist
                        ? "bg-[#FFD700] border-[#FFD700] text-black"
                        : "border-white/20 hover:border-[#FFD700] hover:text-[#FFD700]"
                    }`}
                    data-testid="wishlist-toggle-btn"
                  >
                    <Heart className={`w-6 h-6 ${inWishlist ? "fill-current" : ""}`} />
                  </button>
                </div>
              </>
            ) : (
              <div className="border border-[#FFD700]/30 p-6" data-testid="notify-me-section">
                <div className="flex items-center gap-2 mb-3">
                  <Bell className="w-5 h-5 text-[#FFD700]" />
                  <p className="font-heading text-lg">OUT OF STOCK</p>
                </div>
                {notified ? (
                  <p className="text-[#FFD700] font-mono text-sm">You're on the list! We'll email you when it's back.</p>
                ) : (
                  <form onSubmit={handleNotifyMe} className="space-y-3">
                    <p className="text-[#A3A3A3] text-sm">Get notified when this product is back in stock.</p>
                    <input
                      type="email"
                      value={notifyEmail}
                      onChange={(e) => setNotifyEmail(e.target.value)}
                      placeholder="Enter your email"
                      className="w-full bg-transparent border border-white/20 px-4 py-3 text-white placeholder-white/30 focus:border-[#FFD700] focus:outline-none transition-colors font-mono text-sm"
                      required
                      data-testid="notify-email"
                    />
                    <button
                      type="submit"
                      disabled={notifying}
                      className="w-full bg-[#FFD700] text-black font-heading py-3 tracking-wider hover:bg-[#FFC000] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                      data-testid="notify-me-btn"
                    >
                      <Bell className="w-4 h-4" />
                      {notifying ? "SUBSCRIBING..." : "NOTIFY ME WHEN AVAILABLE"}
                    </button>
                  </form>
                )}
              </div>
            )}

            {/* Stock & Sold */}
            <div className="border-t border-white/10 pt-6 space-y-2">
              {product.stock > 0 ? (
                <div>
                  <p className="font-mono text-sm text-green-500 mb-2">● In Stock</p>
                  {product.stock_by_size && (
                    <div className="flex gap-3 flex-wrap">
                      {Object.entries(product.stock_by_size).map(([size, qty]) => (
                        <span key={size} className={`font-mono text-xs ${qty > 0 ? 'text-[#A3A3A3]' : 'text-red-500'}`}>
                          {size}: {qty > 0 ? qty : 'sold out'}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <p className="font-mono text-sm text-red-500">● Out of Stock</p>
              )}
            </div>
          </motion.div>
        </div>

        {/* Why 3T Section */}
        <div className="mt-16 border-t border-white/10 pt-12">
          <p className="font-mono text-[#FFD700] text-sm tracking-[0.3em] mb-4">WHY 3T?</p>
          <div className="max-w-[700px] space-y-4 text-[#C0C0C0] text-base leading-relaxed">
            <p>We don't believe a shirt changes a life.<br />People do.</p>
            <p>Sometimes all they need is a reminder.</p>
            <p>That's why every 3T shirt begins with an idea worth remembering.</p>
          </div>
        </div>

        {/* Product Description */}
        <div className="mt-12">
          <p className="font-mono text-[#FFD700] text-sm tracking-[0.3em] mb-4">ABOUT THIS SHIRT</p>
          <p className="text-[#C0C0C0] text-base leading-relaxed max-w-[700px]" data-testid="product-description">
            {product.description.split('\n\n')[0]}
          </p>
        </div>

        {/* Fabric Details & Size Guide */}
        <div className="mt-16 space-y-16">
          {/* Fabric Details */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="font-mono text-[#FFD700] text-sm tracking-[0.3em] mb-2">PREMIUM QUALITY</p>
            <h2 className="font-heading text-3xl md:text-4xl mb-2">FABRIC DETAILS</h2>
            <p className="text-[#A3A3A3] text-sm font-mono mb-8">275 GSM / 8.1 OZ — 100% COTTON</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                "https://customer-assets.emergentagent.com/job_759b79d2-1077-4774-b05d-81a2e2b48ae9/artifacts/mu7r8rp4_Tank%20Fabric1.jpg",
                "https://customer-assets.emergentagent.com/job_759b79d2-1077-4774-b05d-81a2e2b48ae9/artifacts/tpzp2a8l_Tank%20Fabric2.jpg",
                "https://customer-assets.emergentagent.com/job_759b79d2-1077-4774-b05d-81a2e2b48ae9/artifacts/vb6xslrx_Tank%20Fabric3.jpg",
                "https://customer-assets.emergentagent.com/job_759b79d2-1077-4774-b05d-81a2e2b48ae9/artifacts/rsno3x67_Tank%20Fabric4.jpg",
              ].map((img, i) => (
                <div key={i} className="aspect-square overflow-hidden border border-white/10">
                  <img src={img} alt={`Fabric detail ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
                </div>
              ))}
            </div>
            <p className="text-[#A3A3A3] text-base leading-relaxed mt-8">
              Built to last.<br />
              Heavy cotton, washed for softness, designed to become even better with time.
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="font-mono text-[#FFD700] text-sm tracking-[0.3em] mb-2">FIT REFERENCE</p>
            <h2 className="font-heading text-3xl md:text-4xl mb-8">SIZE GUIDE</h2>
            <div className="border border-white/10 overflow-hidden">
              <img
                src="https://customer-assets.emergentagent.com/job_759b79d2-1077-4774-b05d-81a2e2b48ae9/artifacts/60ut1urb_3T_size_guide_logo_corrected.png"
                alt="3T Size Guide"
                className="w-full h-auto"
                loading="lazy"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
